import type { Neighborhood } from "../../types/neighborhood";
import type { ServiceAreaSearchCenter } from "./serviceAreaSearchCenter";

export function neighborhoodKey(neighborhood: Neighborhood): string {
  if (neighborhood.placeId) {
    return neighborhood.placeId;
  }
  return `${neighborhood.name.trim().toLowerCase()}|${neighborhood.lat}|${neighborhood.lng}`;
}

export function neighborhoodDisplayLabel(neighborhood: Neighborhood): string {
  const name = neighborhood.name.trim();
  const region = neighborhood.regionLabel?.trim();
  return region ? `${name} (${region})` : name;
}

/** Name persisted when the user confirms a search result with regional context. */
export function neighborhoodStorageName(neighborhood: Neighborhood): string {
  return neighborhoodDisplayLabel(neighborhood);
}

export function distanceKm(
  from: ServiceAreaSearchCenter,
  to: Pick<Neighborhood, "lat" | "lng">,
): number {
  const earthRadiusKm = 6371;
  const lat1 = (from.lat * Math.PI) / 180;
  const lat2 = (to.lat * Math.PI) / 180;
  const deltaLat = ((to.lat - from.lat) * Math.PI) / 180;
  const deltaLng = ((to.lng - from.lng) * Math.PI) / 180;

  const a =
    Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(lat1) *
      Math.cos(lat2) *
      Math.sin(deltaLng / 2) *
      Math.sin(deltaLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return earthRadiusKm * c;
}

export function sortNeighborhoodsByDistance(
  neighborhoods: Neighborhood[],
  center: ServiceAreaSearchCenter | null,
): Neighborhood[] {
  if (!center) {
    return neighborhoods;
  }

  return [...neighborhoods].sort(
    (a, b) => distanceKm(center, a) - distanceKm(center, b),
  );
}
